import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Skeleton,
  Alert,
  AlertTitle,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  fetchGlobalAnalysis,
  getStudentsByClass,
  getSubjectsByClass,
} from "../../utils/analysisApi";
import OverviewCards from "../../components/analysis-layout/overviewCard";
import SelectorPanel from "../../components/analysis-layout/sectorPanel";
import TrendChart from "../../components/analysis-layout/trendingChart";
import ClassChart from "../../components/analysis-layout/classChart";
import StudentLeaderboard from "../../components/analysis-layout/studentLeaderboard";
import SubjectChart from "../../components/analysis-layout/subjectChart";
import Filter from "../../components/analysis-layout/filterbar";

const MotionBox = motion(Box);

/* ─── ambient orb background ─── */
const AmbientBackground = () => (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      zIndex: 0,
      pointerEvents: "none",
      overflow: "hidden",
      background:
        "linear-gradient(160deg, #f0f4ff 0%, #fafbff 40%, #f5f0ff 100%)",
    }}
  >
    {/* top-left orb */}
    <Box
      sx={{
        position: "absolute",
        top: "-180px",
        left: "-120px",
        width: 560,
        height: 560,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(99,102,241,0.13) 0%, transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    {/* top-right orb */}
    <Box
      sx={{
        position: "absolute",
        top: "-80px",
        right: "-60px",
        width: 440,
        height: 440,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(16,185,129,0.10) 0%, transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    {/* center orb */}
    <Box
      sx={{
        position: "absolute",
        top: "40%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        width: 700,
        height: 500,
        borderRadius: "50%",
        background:
          "radial-gradient(ellipse, rgba(139,92,246,0.06) 0%, transparent 70%)",
        filter: "blur(60px)",
      }}
    />
    {/* subtle dot grid */}
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        backgroundImage: `radial-gradient(circle, rgba(99,102,241,0.08) 1px, transparent 1px)`,
        backgroundSize: "28px 28px",
        maskImage:
          "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)",
      }}
    />
  </Box>
);

/* ─── dashboard header ─── */
const DashboardHeader = () => (
  <MotionBox
    initial={{ opacity: 0, y: -24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    sx={{ mb: 4 }}
  >
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      gap={2}
    >
      <Box>
        <Stack direction="row" alignItems="center" spacing={2} mb={0.5}>
          {/* icon badge */}
          <MotionBox
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            sx={{
              width: 52,
              height: 52,
              borderRadius: "18px",
              background:
                "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 8px 24px rgba(99,102,241,0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
              fontSize: "1.5rem",
              flexShrink: 0,
            }}
          >
            📊
          </MotionBox>
          <Box>
            <Typography
              sx={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 800,
                fontSize: { xs: "1.6rem", sm: "2rem" },
                color: "#0f0f23",
                lineHeight: 1.1,
                letterSpacing: "-0.04em",
                background:
                  "linear-gradient(135deg, #1e1b4b 0%, #6366f1 60%, #8b5cf6 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Analytics Hub
            </Typography>
            <Typography
              sx={{
                color: "#6b7280",
                fontSize: "0.82rem",
                fontWeight: 500,
                letterSpacing: "0.02em",
              }}
            >
              Academic performance intelligence
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* live badge */}
      <MotionBox
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
        sx={{
          px: 2.5,
          py: 1,
          borderRadius: "100px",
          backdropFilter: "blur(16px)",
          background: "rgba(255,255,255,0.7)",
          border: "1px solid rgba(99,102,241,0.2)",
          boxShadow:
            "0 4px 20px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.8)",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <MotionBox
          animate={{ opacity: [1, 0.3, 1], scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #10b981, #059669)",
          }}
        />
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "#6366f1",
            letterSpacing: "0.05em",
          }}
        >
          LIVE DATA
        </Typography>
      </MotionBox>
    </Stack>
  </MotionBox>
);

/* ─── skeleton ─── */
const FullPageSkeleton = () => (
  <Box sx={{ position: "relative", minHeight: "100vh" }}>
    <AmbientBackground />
    <Container maxWidth="xl" sx={{ py: 4, position: "relative", zIndex: 1 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={5}>
        <Skeleton
          variant="rounded"
          width={52}
          height={52}
          sx={{ borderRadius: "18px" }}
        />
        <Box>
          <Skeleton width={200} height={36} />
          <Skeleton width={280} height={16} sx={{ mt: 0.5 }} />
        </Box>
      </Stack>
      <Skeleton
        variant="rounded"
        width="100%"
        height={72}
        sx={{ borderRadius: "20px", mb: 3 }}
      />
      <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mb: 3 }}>
        {[...Array(3)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            sx={{ flex: "1 1 200px", height: 130, borderRadius: "24px" }}
          />
        ))}
      </Box>
      <Skeleton
        variant="rounded"
        width="100%"
        height={320}
        sx={{ borderRadius: "24px", mb: 3 }}
      />
      <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap" }}>
        <Skeleton
          variant="rounded"
          sx={{ flex: "1 1 340px", height: 360, borderRadius: "24px" }}
        />
        <Skeleton
          variant="rounded"
          sx={{ flex: "1 1 340px", height: 360, borderRadius: "24px" }}
        />
      </Box>
    </Container>
  </Box>
);

/* ─── error state ─── */
const ErrorState = ({ onRetry }) => (
  <Container maxWidth="xl" sx={{ py: 4 }}>
    <Alert
      severity="error"
      sx={{
        borderRadius: "20px",
        border: "1px solid rgba(220,38,38,0.2)",
        backdropFilter: "blur(12px)",
        background: "rgba(254,242,242,0.8)",
      }}
      action={
        <Button
          color="error"
          size="small"
          onClick={onRetry}
          sx={{ fontWeight: 700, borderRadius: "10px" }}
        >
          Retry
        </Button>
      }
    >
      <AlertTitle sx={{ fontWeight: 700, fontFamily: "'Syne', sans-serif" }}>
        Failed to load dashboard data
      </AlertTitle>
      An error occurred while fetching analysis data. Please try again.
    </Alert>
  </Container>
);

/* ─── main component ─── */
export default function AnalysisDashboard() {
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingDependents, setLoadingDependents] = useState(false);
  const [filters, setFilters] = useState({ term: "", session: "" });
  const navigate = useNavigate();

  const loadData = () => {
    setLoading(true);
    setError(null);
    fetchGlobalAnalysis(filters?.term, filters?.session)
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, [filters]); // eslint-disable-line

  useEffect(() => {
    if (selectedClass) {
      setLoadingDependents(true);
      Promise.all([
        getStudentsByClass(selectedClass),
        getSubjectsByClass(selectedClass),
      ])
        .then(([studentsRes, subjectsRes]) => {
          setStudents(studentsRes.students || studentsRes);
          setSubjects(subjectsRes.subjects || subjectsRes);
          setLoadingDependents(false);
        })
        .catch(() => setLoadingDependents(false));
    } else {
      setStudents([]);
      setSubjects([]);
    }
  }, [selectedClass]);

  if (loading) return <FullPageSkeleton />;
  if (error) return <ErrorState onRetry={loadData} />;
  if (!data) return <FullPageSkeleton />;

  return (
    <Container maxWidth={false} sx={{ maxWidth: '72rem' }}>
    <Box sx={{ position: "relative", minHeight: "100vh" }}>
      <AmbientBackground />
      <Container maxWidth="xl" sx={{ py: 4, position: "relative", zIndex: 1 }}>
        <DashboardHeader />
        <Filter filters={filters} setFilters={setFilters} data={data} />
        <OverviewCards overview={data.overview} />
        <SelectorPanel
          data={data}
          selectedClass={selectedClass}
          setSelectedClass={setSelectedClass}
          selectedSubject={selectedSubject}
          setSelectedSubject={setSelectedSubject}
          selectedStudent={selectedStudent}
          setSelectedStudent={setSelectedStudent}
          subjects={subjects}
          students={students}
          loadingDependents={loadingDependents}
          filters={filters}
          navigate={navigate}
        />
        <TrendChart data={data.trend} />
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <Box sx={{ flex: "1 1 340px", minWidth: 0 }}>
            <ClassChart
              data={data.class_performance}
              classes={data.classes}
              filters={filters}
            />
          </Box>
          <Box sx={{ flex: "1 1 340px", minWidth: 0 }}>
            <SubjectChart data={data.subject_performance} filters={filters} />
          </Box>
        </Box>
        <StudentLeaderboard
          students={data.top_students}
          filters={filters}
          // onNavigate={(student) => {
          //   const q = [];
          //   if (filters.term) q.push(`term=${filters.term}`);
          //   if (filters.session) q.push(`session=${filters.session}`);
          //   navigate(
          //     `/analysis/student/${student.id}${
          //       q.length ? `?${q.join("&")}` : ""
          //     }`
          //   );
          // }}
        />
      </Container>
    </Box>
    </Container>
  );
}
